import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../services/assistant-api';

import { useFormState } from '../../../hooks/useFormState';

import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../constants/errorMessages';

import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import ErrorMessage from '../../../design-system/ErrorMessage/ErrorMessage';
import EditLineIcon from '../../../design-system/Icons/EditLineIcon';
import TextAreaWithAutoResize from '../../../design-system/TextAreaWithAutoResize/TextAreaWithAutoResize';
import PromptTabActionButtons from '../PromptTabActionButtons/PromptTabActionButtons';
import { SvgIcon } from '../../../design-system';

const PromptTabSection = ({ promptData, setPromptsData }) => {
    const { data, error } = promptData || {};
    const prompt = data?.default_version?.messages?.[0]?.content;
    const promptId = data?.id;

    const { formData, setFormData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } =
        useFormState(null);
    const [isEditing, setIsEditing] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    useEffect(() => {
        if (!data || !!formData) return;

        return setFormData({ prompt });
    }, [data]);

    const onSave = async () => {
        if (prompt === formData.prompt) {
            setIsEditing(false);
            return;
        }
        try {
            const promptContent = formData.prompt?.trim();

            if (!promptContent) {
                setFieldErrorMessages({ prompt: emptyFieldErrorMessage });
                return;
            }

            setIsLoading(true);

            const requestBody = {
                messages: [
                    {
                        role: 'user',
                        content: promptContent,
                    },
                ],
            };

            const { data } = await client.patch(
                `${API.ROUTES.assistant.chatPrompt}${promptId}/`,
                requestBody
            );

            setPromptsData((prevData) => ({ ...prevData, [promptId]: { data } }));
            setIsEditing(false);
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const onCancel = () => {
        setIsEditing(false);
        handleInputChange('prompt', prompt);
    };

    if (error) return <ErrorMessage message={defaultErrorMessage} size="medium" />;

    if (!promptData || !formData) return null;

    const state = !isEditing ? 'disabled' : fieldErrorMessages.content ? 'error' : 'default';

    const isPromptEmpty = !formData?.prompt?.trim();
    const cancelButtonState = isLoading ? 'disabled' : 'default';
    const saveButtonState = isLoading ? 'loading' : isPromptEmpty ? 'disabled' : 'default';

    const className = classNames('relative', state === 'disabled' && 'group');
    const textareaClassName = classNames(
        state === 'disabled' && '!bg-neutral-50/50 !border-neutral-200 !focus:shadow-neutral-200'
    );

    return (
        <>
            <div className={className}>
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button
                        onClick={() => setIsEditing(true)}
                        className="w-[28px] h-[28px] flex items-center justify-center rounded-2 bg-neutral-200"
                    >
                        <SvgIcon color="#1F2125" icon={EditLineIcon} size="medium" />
                    </button>
                </div>

                <TextAreaWithAutoResize
                    name="prompt_content"
                    value={formData.prompt}
                    state={state}
                    errorMessage={fieldErrorMessages.prompt}
                    placeholder="Enter your prompt here"
                    customClassName={textareaClassName}
                    onChange={(e) => handleInputChange('prompt', e.target.value)}
                />
            </div>

            <PromptTabActionButtons
                isEditing={isEditing}
                prompt={prompt}
                promptId={promptId}
                onSave={onSave}
                onCancel={onCancel}
                saveButtonState={saveButtonState}
                cancelButtonState={cancelButtonState}
            />

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </>
    );
};

export default PromptTabSection;
